// `data.getTabName` — the tab label for states whose tab is named after a ROW rather than after
// the screen.
//
// ⚠️ IT IS THE ONE PIECE OF `data` THAT IS NOT A VALUE. `tabName: 'Searches'` is a literal and
// comes out of `routes.json`; the candidate and client-contact profiles declare
// `getTabName: (candidateContainer) => candidateContainer.getCandidate().then(getCandidateName,
// () => 'Candidate profile')` instead — a function, invoked by the tab service with the state's
// own resolved dependencies. `tabNameFor` returns undefined for those states, and an
// undefined tab name renders NO TAB AT ALL: the candidate profile came out with an empty strip
// above a correct screen, which is 967 pixels and reads as a styling defect.
//
// Upstream's tab service outlives navigation and holds each group's label; this is that store,
// as small as it can be. The route that owns the row publishes the label once it has it, and
// the layout — which has no business fetching a candidate — reads it.
import { useSyncExternalStore } from 'react'
import type { ExtensionTabGroup } from '@woo-ext/ui'

const names = new Map<ExtensionTabGroup, string>()
const listeners = new Set<() => void>()

function emit(): void {
  for (const listener of listeners) listener()
}

/**
 * Publish (or clear, with `undefined`) the label for a group's tab.
 *
 * ⚠️ THE FALLBACK IS THE CALLER'S, NOT THIS STORE'S. Upstream's promise rejects to
 * 'Candidate profile' — a real label for a tab that exists whether or not the row loads — so a
 * route that cannot name its candidate publishes that string rather than nothing. Defaulting
 * here would put the word "Candidate profile" on a tab whose route has not opened yet.
 */
export function publishTabName(group: ExtensionTabGroup, text: string | undefined): void {
  if (text === undefined) {
    if (!names.delete(group)) return
  } else {
    if (names.get(group) === text) return
    names.set(group, text)
  }
  emit()
}

/** The `useSyncExternalStore` half of the store; exported because a test subscribes too. */
export function subscribeToTabNames(listener: () => void): () => void {
  listeners.add(listener)
  return () => {
    listeners.delete(listener)
  }
}

/** The published label for `group`, or undefined while nothing has published one. */
export function tabNameOf(group: ExtensionTabGroup | undefined): string | undefined {
  return group === undefined ? undefined : names.get(group)
}

/** The published label for `group`, or undefined while nothing has published one. */
export function usePublishedTabName(group: ExtensionTabGroup | undefined): string | undefined {
  return useSyncExternalStore(
    subscribeToTabNames,
    () => tabNameOf(group),
    () => undefined,
  )
}

/** Test seam — the store is module state, so a suite has to be able to empty it. */
export function resetTabNames(): void {
  if (names.size === 0) return
  names.clear()
  emit()
}
